import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RINGERS_API_URL } from '../config';

const API_URL = RINGERS_API_URL;

function MaintenancePage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({ name: '', sort_order: 0 });
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [importCounts, setImportCounts] = useState(null);
  const [sqlImporting, setSqlImporting] = useState(false);
  const [sqlImportMsg, setSqlImportMsg] = useState('');
  const [sqlImportCounts, setSqlImportCounts] = useState(null);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await fetch(`${API_URL}/groups`);
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      } else {
        setError('Failed to fetch groups');
      }
    } catch (e) {
      setError('Failed to fetch groups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) return;
    
    try {
      const response = await fetch(`${API_URL}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newGroup)
      });
      
      if (response.ok) {
        setNewGroup({ name: '', sort_order: 0 });
        fetchGroups();
      } else {
        setError('Failed to create group');
      }
    } catch (e) {
      setError('Failed to create group');
    }
  };

  const handleUpdateGroup = async (group) => {
    try {
      const response = await fetch(`${API_URL}/groups/${group.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(group)
      });
      
      if (response.ok) {
        setEditingGroup(null);
        fetchGroups();
      } else {
        setError('Failed to update group');
      }
    } catch (e) {
      setError('Failed to update group');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    
    try {
      const response = await fetch(`${API_URL}/groups/${groupId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchGroups();
      } else {
        setError('Failed to delete group');
      }
    } catch (e) {
      setError('Failed to delete group');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setExportMsg('');
    try {
      const response = await fetch(`${API_URL}/export`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Generate filename with timestamp (YYYYMMDD_HHMMSS format)
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const timestamp = `${year}${month}${day}_${hours}${minutes}${seconds}`;
        const filename = `hancock_handbells_export_${timestamp}.zip`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setExportMsg('Export completed successfully!');
      } else {
        setExportMsg('Export failed');
      }
    } catch (e) {
      setExportMsg('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    setImportMsg('Importing...');
    setImportCounts(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/import`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setImportMsg('Import completed successfully!');
        if (data.counts) {
          setImportCounts(data.counts);
        }
        // Navigate to ringers page after a short delay to show the message
        setTimeout(() => {
          navigate('/ringers/maintenance');
        }, 10000);
      } else {
        setImportMsg('Import failed');
        setImportCounts(null);
      }
    } catch (e) {
      setImportMsg('Import failed');
      setImportCounts(null);
    } finally {
      setImporting(false);
    }
  };

  const handleSqlImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!window.confirm('This will import SQL Server CSV data into PostgreSQL. This will replace all existing data. Continue?')) {
      e.target.value = ''; // Reset file input
      return;
    }
    
    setSqlImporting(true);
    setSqlImportMsg('Importing SQL Server CSV data... This may take several minutes.');
    setSqlImportCounts(null);
    setError(''); // Clear any previous errors
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log('Starting SQL import request to:', `${API_URL}/import-sql`);
      console.log('File name:', file.name, 'File size:', file.size, 'bytes');
      
      const response = await fetch(`${API_URL}/import-sql`, {
        method: 'POST',
        body: formData,
        // Note: Browser fetch API doesn't support timeout directly
        // The server should handle long-running requests
      });
      
      console.log('Response received:', response.status, response.statusText);
      
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = 'Import failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
        } catch (parseError) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        setSqlImportMsg(`SQL Import failed: ${errorMessage}`);
        setError(errorMessage);
        setSqlImportCounts(null);
        return;
      }
      
      const data = await response.json();
      setSqlImportMsg('SQL Import completed successfully!');
      if (data.counts) {
        setSqlImportCounts(data.counts);
      }
      setError(''); // Clear any previous errors
      // Navigate to ringers page after a short delay to show the message
      setTimeout(() => {
        navigate('/ringers/maintenance');
      }, 10000);
    } catch (e) {
      // Network error or other fetch errors
      console.error('SQL Import error details:', {
        error: e,
        message: e.message,
        stack: e.stack,
        name: e.name,
        apiUrl: `${API_URL}/import-sql`,
        fileName: file.name,
        fileSize: file.size
      });
      
      const errorMsg = e.message || 'Network error: failed to fetch. Please check: 1) Backend server is running on port 8000, 2) Check browser console for CORS errors, 3) Check backend console for errors.';
      setSqlImportMsg(`SQL Import failed: ${errorMsg}`);
      setError(errorMsg);
      setSqlImportCounts(null);
    } finally {
      setSqlImporting(false);
      e.target.value = ''; // Reset file input
    }
  };

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Maintenance</h1>
        <Link to="/" style={{ padding: '10px 16px', backgroundColor: '#ffc107', color: 'black', textDecoration: 'none', borderRadius: '5px', fontWeight: 'bold' }}>
          Back to Home
        </Link>
      </div>

      {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}

      {/* Data Management Section */}
      <div style={{ marginBottom: '3rem' }}>
        <h2>Data Management</h2>
        
        {/* Export Section */}
        <div style={{ marginBottom: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '5px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Export Database</h3>
          <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
            Export all database data to a zip file containing CSV files. This creates a backup that can be imported later.
          </p>
          <button 
            onClick={handleExport}
            disabled={exporting}
            style={{ 
              padding: '10px 16px', 
              backgroundColor: exporting ? '#cccccc' : '#28a745', 
              color: exporting ? '#666666' : 'white', 
              border: 'none', 
              borderRadius: '5px', 
              cursor: exporting ? 'not-allowed' : 'pointer',
              fontWeight: 'bold'
            }}
          >
            {exporting ? 'Exporting...' : 'Export Database to CSV'}
          </button>
          {exportMsg && <div style={{ color: exportMsg.includes('failed') ? 'red' : 'green', marginTop: '0.5rem' }}>{exportMsg}</div>}
        </div>

        {/* Import Section */}
        <div style={{ marginBottom: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '5px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Import Database</h3>
          <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
            Import data from a previously exported zip file. This will replace all existing data in the database.
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <input
              type="file"
              accept=".zip"
              onChange={handleImport}
              disabled={importing || sqlImporting}
              style={{ display: 'none' }}
              id="import-file"
            />
            <label 
              htmlFor="import-file"
              style={{ 
                padding: '10px 16px', 
                backgroundColor: importing ? '#cccccc' : '#17a2b8',
                color: importing ? '#666666' : 'white',
                fontWeight: 'bold', 
                border: 'none', 
                borderRadius: '5px', 
                cursor: importing ? 'not-allowed' : 'pointer',
                display: 'inline-block'
              }}
            >
              {importing ? 'Importing...' : 'Import from CSV Export'}
            </label>
          </div>
          {importMsg && (
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ color: importMsg.includes('failed') ? 'red' : 'green', fontWeight: 'bold' }}>{importMsg}</div>
              {importCounts && (
                <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#e9ecef', borderRadius: '5px', fontSize: '0.9rem' }}>
                  <strong>Import Summary:</strong>
                  <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                    {importCounts.groups > 0 && <li>Groups: {importCounts.groups}</li>}
                    {importCounts.persons > 0 && <li>Persons: {importCounts.persons}</li>}
                    {importCounts.memberships > 0 && <li>Group Memberships: {importCounts.memberships}</li>}
                    {importCounts.phones > 0 && <li>Phone Numbers: {importCounts.phones}</li>}
                    {importCounts.emails > 0 && <li>Email Addresses: {importCounts.emails}</li>}
                    {importCounts.pieces > 0 && <li>Pieces: {importCounts.pieces}</li>}
                    {importCounts.performances > 0 && <li>Performances: {importCounts.performances}</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* SQL Import Section */}
        <div style={{ marginBottom: '2rem', padding: '1rem', background: '#fff3cd', borderRadius: '5px', border: '1px solid #ffc107' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Import from SQL Server Database</h3>
          <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
            Import data from a legacy SQL Server database export (zip file with CSV files: BellGroups.csv, BellMembers.csv, etc.). 
            This will <strong>replace all existing data</strong> in the database. The import may take several minutes to complete.
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <input
              type="file"
              accept=".zip"
              onChange={handleSqlImport}
              disabled={importing || sqlImporting}
              style={{ display: 'none' }}
              id="sql-import-file"
            />
            <label 
              htmlFor="sql-import-file"
              style={{ 
                padding: '10px 16px', 
                backgroundColor: sqlImporting ? '#cccccc' : '#6f42c1',
                color: sqlImporting ? '#666666' : 'white',
                fontWeight: 'bold', 
                border: 'none', 
                borderRadius: '5px', 
                cursor: sqlImporting ? 'not-allowed' : 'pointer',
                display: 'inline-block'
              }}
            >
              {sqlImporting ? 'Importing SQL Data...' : 'Import from SQL Server CSV'}
            </label>
          </div>
          {sqlImportMsg && (
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ color: sqlImportMsg.includes('failed') ? 'red' : 'green', fontWeight: 'bold' }}>{sqlImportMsg}</div>
              {sqlImportCounts && (
                <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#e9ecef', borderRadius: '5px', fontSize: '0.9rem' }}>
                  <strong>Import Summary:</strong>
                  <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                    {sqlImportCounts.groups > 0 && <li>Groups: {sqlImportCounts.groups}</li>}
                    {sqlImportCounts.persons > 0 && <li>Persons: {sqlImportCounts.persons}</li>}
                    {sqlImportCounts.memberships > 0 && <li>Group Memberships: {sqlImportCounts.memberships}</li>}
                    {sqlImportCounts.phones > 0 && <li>Phone Numbers: {sqlImportCounts.phones}</li>}
                    {sqlImportCounts.emails > 0 && <li>Email Addresses: {sqlImportCounts.emails}</li>}
                    {sqlImportCounts.pieces > 0 && <li>Pieces: {sqlImportCounts.pieces}</li>}
                    {sqlImportCounts.performances > 0 && <li>Performances: {sqlImportCounts.performances}</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Group Management Section */}
      <div>
        <h2>Group Management</h2>
        
        {/* Create new group */}
        <div style={{ marginBottom: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '5px' }}>
          <h3>Add New Group</h3>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Group name"
              value={newGroup.name}
              onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
              style={{ padding: '8px', borderRadius: '3px', border: '1px solid #ddd' }}
            />
            <input
              type="number"
              placeholder="Sort order"
              value={newGroup.sort_order}
              onChange={(e) => setNewGroup({ ...newGroup, sort_order: parseInt(e.target.value) || 0 })}
              style={{ padding: '8px', borderRadius: '3px', border: '1px solid #ddd', width: '100px' }}
            />
            <button 
              onClick={handleCreateGroup}
              style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Add Group
            </button>
          </div>
        </div>

        {/* List groups */}
        {loading ? (
          <div>Loading groups...</div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {groups.map(group => (
              <div key={group.id} style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '5px' }}>
                {editingGroup?.id === group.id ? (
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={editingGroup.name}
                      onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })}
                      style={{ padding: '8px', borderRadius: '3px', border: '1px solid #ddd' }}
                    />
                    <input
                      type="number"
                      value={editingGroup.sort_order}
                      onChange={(e) => setEditingGroup({ ...editingGroup, sort_order: parseInt(e.target.value) || 0 })}
                      style={{ padding: '8px', borderRadius: '3px', border: '1px solid #ddd', width: '100px' }}
                    />
                    <button 
                      onClick={() => handleUpdateGroup(editingGroup)}
                      style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      Save
                    </button>
                    <button 
                      onClick={() => setEditingGroup(null)}
                      style={{ padding: '8px 16px', backgroundColor: '#ffc107', color: 'black', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong>{group.name}</strong> (Sort: {group.sort_order})
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        onClick={() => setEditingGroup(group)}
                        style={{ padding: '4px 8px', backgroundColor: '#ffc107', color: 'black', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteGroup(group.id)}
                        style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MaintenancePage;

